// queryClient.ts — app-wide QueryClient with global error toasting.
// Extracted from App.tsx so the cache handlers are unit-testable.
import { QueryClient, MutationCache, QueryCache } from '@tanstack/react-query';
import { toastBus } from '@/foundation/toastBus';
import { ApiError, apiErrorDetail } from '@/api/client';

export function createAppQueryClient(): QueryClient {
  let lastQueryToast = { msg: '', at: 0 };

  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        // One retry for transient failures — never for a 403: the role or scope won't change on retry,
        // it only doubles the refused request.
        retry: (failureCount, error) =>
          !(error instanceof ApiError && error.status === 403) && failureCount < 1,
      },
    },
    // reads fail loudly too — a 403 from an out-of-scope store must not render
    // as a silently empty table (deduped: polling/refetch repeats one toast)
    queryCache: new QueryCache({
      onError: (error) => {
        // 403s are store-scope issues surfaced inline by the list/table empty state
        // (see common.tsx) — don't also pop a global toast on every background query.
        if (error instanceof ApiError && error.status === 403) return;
        const msg =
          error instanceof ApiError
            ? `Couldn't load data (${error.status})`
            : error.message || "Couldn't load data";
        const now = Date.now();
        if (lastQueryToast.msg === msg && now - lastQueryToast.at < 5000) return;
        lastQueryToast = { msg, at: now };
        toastBus.emit(msg, 'error');
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        // A mutation with a hook-level onError owns the user feedback for the
        // failure — toasting here too produced a duplicate toast per error.
        // (mutate()-call-level callbacks are invisible here; those toast the
        // same apiErrorDetail() text and the store collapses the duplicate.)
        if (mutation.options.onError) return;
        toastBus.emit(apiErrorDetail(error), 'error');
      },
    }),
  });
}
